<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="12">
                    <h1>Marketplace Order Details</h1>
                </v-col>
            </v-row>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="data.showDeleteDialog" max-width="500px">
                <v-card>
                    <v-card-title class="text-h5"> Confirm Deletion </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this marketplace order item?
                        <br /><br />
                        <strong>SKU:</strong> {{ data.itemToDelete?.sku || 'N/A' }}
                        <br />
                        <strong>Quantity:</strong> {{ data.itemToDelete?.quantity || 'N/A' }} <br /><br />
                        This action cannot be undone.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey darken-1" text @click="cancelDelete"> Cancel </v-btn>
                        <v-btn color="red darken-1" text @click="confirmDelete"> Delete </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Marketplace Order Details Section -->
            <v-card class="mb-6">
                <v-card-title>
                    <h2>Marketplace Order Information</h2>
                </v-card-title>
                <v-card-text>
                    <v-row v-if="data.marketplaceOrder">
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.orderNumber"
                                :label="formLabelText.orderNumber"
                                readonly
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.marketplaceCode"
                                :label="formLabelText.marketplaceCode"
                                readonly
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.orderBalanceAmount"
                                :label="formLabelText.orderBalanceAmount"
                                readonly
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <strong>{{ formLabelText.orderStatus }}:</strong> {{ data.marketplaceOrder.orderStatus }}
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <strong>{{ formLabelText.refundStatus }}:</strong> {{ data.marketplaceOrder.refundStatus }}
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <strong>{{ formLabelText.paymentStatus }}:</strong>
                            {{ data.marketplaceOrder.paymentStatus }}
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.dateOrderOn"
                                :label="formLabelText.dateOrderOn"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.orderAmount"
                                :label="formLabelText.orderAmount"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.orderCurrencyCode"
                                :label="formLabelText.orderCurrencyCode"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.commissionPercentage"
                                :label="formLabelText.commissionPercentage"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.commissionAmount"
                                :label="formLabelText.commissionAmount"
                                readonly
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.commissionCurrencyCode"
                                :label="formLabelText.commissionCurrencyCode"
                                readonly
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.transactionFee"
                                :label="formLabelText.transactionFee"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.transactionFeeCurrencyCode"
                                :label="formLabelText.transactionFeeCurrencyCode"
                                readonly
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4"> </v-col>
                        <v-col cols="12" sm="12" md="12">
                            <v-textarea
                                v-model="data.marketplaceOrder.remarks"
                                :label="formLabelText.remarks"
                                rows="3"
                            ></v-textarea>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <strong>{{ formLabelText.dateCreateAt }}:</strong>
                            {{ formatDateTime(data.marketplaceOrder.dateCreateAt) }}
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <strong>{{ formLabelText.dateModifyAt }}:</strong>
                            {{ formatDateTime(data.marketplaceOrder.dateModifyAt) }}
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex justify-end">
                                <v-btn color="primary" @click="onSaveMarketplaceOrderClick"> Save Changes </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="12">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                            Loading marketplace order information...
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Shipping Fee Section -->
            <v-card class="mb-6">
                <v-card-title>
                    <h2>Shipping Fee Information</h2>
                </v-card-title>
                <v-card-text>
                    <v-row v-if="data.marketplaceOrder">
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="data.marketplaceOrder.shippingFeeAmount"
                                :label="formLabelText.shippingFeeAmount"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex justify-end">
                                <v-btn color="primary" @click="onSaveShippingFeeClick"> Save Shipping Fee </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="12">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                            Loading shipping fee information...
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Marketplace Order Items Section -->
            <v-card>
                <v-card-title>
                    <h2>Marketplace Order Items</h2>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in marketplaceOrderItemHeaders" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in data.orderItemList" :key="item.orderItemId">
                                <td v-for="header in marketplaceOrderItemHeaders" :key="header.value">
                                    <template v-if="header.value !== actionHeaderItem.value">
                                        {{ formatCellValue(item, header) }}
                                    </template>
                                    <v-btn
                                        v-else-if="isActive(item)"
                                        variant="text"
                                        icon="mdi-delete"
                                        color="error"
                                        @click="onDeleteMarketplaceOrderItemClick(item)"
                                    >
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                    <div v-if="!data.orderItemList.length" class="text-center mt-4">No order items found</div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { MarketplaceOrderLabelTextHelper } from '../../utils/labelTextHelper/MarketplaceOrderLabelTextHelper';
import { MarketplaceOrderItemLabelTextHelper } from '../../utils/labelTextHelper/MarketplaceOrderItemLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { TableFormatHelper } from '../../utils/TableFormatHelper';
import { MarketplaceOrderHeaderHelper } from '../../utils/headerHelper/MarketplaceOrderHeaderHelper';
import { MarketplaceOrderItemHeaderHelper } from '../../utils/headerHelper/MarketplaceOrderItemHeaderHelper';
import AlertMessage from '../../components/AlertMessage.vue';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

export default defineComponent({
    name: 'UpdateMarketplaceOrder',

    components: {
        AlertMessage
    },

    setup() {
        const route = useRoute();
        const orderId = route.params.orderId;

        const data = reactive({
            errorAlertMessage: '',
            marketplaceOrder: null,
            orderItemList: [],
            showDeleteDialog: false,
            itemToDelete: null
        });

        onMounted(async () => {
            await Promise.all([loadMarketplaceOrderDetails(), loadMarketplaceOrderItems()]);
            await loadProductCostsForItems();
        });

        const loadMarketplaceOrderDetails = async () => {
            try {
                const requestUrl = `/api/staff/marketplaceOrder/getById?orderId=${orderId}`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    const order = response.result;
                    order.dateOrderOn = TableFormatHelper.formatDate(order.dateOrderOn);
                    data.marketplaceOrder = response.result;
                } else {
                    data.errorAlertMessage = response.errors || 'Failed to load marketplace order details';
                    const errorAlert = document.querySelector('alert-message') as any;
                    if (errorAlert) errorAlert.showAlert();
                }
            } catch (error) {
                data.errorAlertMessage = 'An error occurred while fetching marketplace order details';
                const errorAlert = document.querySelector('alert-message') as any;
                if (errorAlert) errorAlert.showAlert();
            }
        };

        const loadMarketplaceOrderItems = async () => {
            try {
                const requestUrl = `/api/staff/marketplaceOrderItem/getByOrderId?orderId=${orderId}`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    data.orderItemList = (response.result || []).map((item: any) => ({
                        ...item,
                        totalItemPrice: Number(item.itemUnitPriceAmount || 0) * (item.quantity || 0)
                    }));
                } else {
                    data.errorAlertMessage = response.errors || 'Failed to load marketplace order items';
                    const errorAlert = document.querySelector('alert-message') as any;
                    if (errorAlert) errorAlert.showAlert();
                }
            } catch (error) {
                data.errorAlertMessage = 'An error occurred while fetching marketplace order items';
                const errorAlert = document.querySelector('alert-message') as any;
                if (errorAlert) errorAlert.showAlert();
            }
        };

        const loadProductCostsForItems = async () => {
            if (!data.marketplaceOrder || !data.orderItemList.length) return;

            const dateOrderOn = data.marketplaceOrder.dateOrderOn;
            const costPromises = data.orderItemList.map(async (item: any) => {
                try {
                    const requestUrl = `/api/staff/productCost/getEffectiveCost?productId=${item.productId}&date=${dateOrderOn}`;
                    const response = await fetchWrapper.get(requestUrl, '');
                    if (response.success && response.result) {
                        item.productCost = Number(response.result.productCostAmount) || 0;
                    } else {
                        item.productCost = 0;
                    }
                } catch {
                    item.productCost = 0;
                }
                item.totalProductCost = item.productCost * (item.quantity || 0);
            });

            await Promise.all(costPromises);
        };

        const itemDetailHeaders = MarketplaceOrderItemHeaderHelper.getHeaderItems().filter((item) =>
            ['sku', 'quantity', 'itemUnitPriceAmount'].includes(item.value)
        );
        const priceHeaders = [
            {
                text: MarketplaceOrderItemLabelTextHelper.getLabelText.totalItemPrice,
                value: 'totalItemPrice'
            }
        ];
        const costHeaders = [
            { text: 'Unit Cost', value: 'productCost' },
            { text: 'Total Cost', value: 'totalProductCost' }
        ];
        const dateHeaders = MarketplaceOrderItemHeaderHelper.getHeaderItems().filter(
            (item) => item.format === 'datetime'
        );
        const marketplaceOrderItemHeaders = [
            ...itemDetailHeaders,
            ...priceHeaders,
            ...costHeaders,
            ...dateHeaders
        ];

        return {
            formLabelText: MarketplaceOrderLabelTextHelper.getLabelText,
            actionHeaderItem: MarketplaceOrderHeaderHelper.getActionHeaderItem(),
            marketplaceOrderItemLabelText: MarketplaceOrderItemLabelTextHelper.getLabelText,
            marketplaceOrderItemHeaders,
            data
        };
    },

    methods: {
        formatCellValue(item: any, header: { value: string; format?: string }) {
            return TableFormatHelper.formatDateTimeCellValue(item, header);
        },
        formatDateTime(iso8601Time: string): string {
            return TableFormatHelper.formatDateTime(iso8601Time);
        },
        formatDate(value: string): string {
            return TableFormatHelper.formatDate(value);
        },
        isActive: function (item: any) {
            return item.isDeleted === false;
        },
        onDeleteMarketplaceOrderItemClick(item: any) {
            this.data.itemToDelete = item;
            this.data.showDeleteDialog = true;
        },
        cancelDelete() {
            this.data.showDeleteDialog = false;
            this.data.itemToDelete = null;
        },
        async confirmDelete() {
            try {
                const item = this.data.itemToDelete;
                const requestUrl = `/api/staff/marketplaceOrderItem/batchDelete`;
                const requestData = {
                    orderItemIdList: [item.orderItemId]
                };
                const response = await fetchWrapper.post(requestUrl, requestData);
                if (response.success) {
                    this.data.errorAlertMessage = 'Marketplace order item deleted successfully!';
                    this.$refs.errorAlert.showAlert('success');
                    this.data.orderItemList = this.data.orderItemList.filter((i) => i.orderItemId !== item.orderItemId);
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to delete marketplace order item';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.errorAlertMessage = 'An error occurred while deleting the marketplace order item';
                this.$refs.errorAlert.showAlert();
            } finally {
                this.data.showDeleteDialog = false;
                this.data.itemToDelete = null;
            }
        },
        async onSaveMarketplaceOrderClick() {
            try {
                const requestUrl = `/api/staff/marketplaceOrder/updateById`;
                const requestData = {
                    orderId: this.data.marketplaceOrder.orderId,
                    dateOrderOn: this.data.marketplaceOrder.dateOrderOn,
                    orderAmount: this.data.marketplaceOrder.orderAmount,
                    orderCurrencyCode: this.data.marketplaceOrder.orderCurrencyCode,
                    commissionPercentage: this.data.marketplaceOrder.commissionPercentage,
                    transactionFee: this.data.marketplaceOrder.transactionFee,
                    remarks: this.data.marketplaceOrder.remarks
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                if (response.success) {
                    this.data.errorAlertMessage = 'Marketplace order updated successfully!';
                    this.$refs.errorAlert.showAlert('success');
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to update marketplace order';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.errorAlertMessage = 'An error occurred while updating the marketplace order';
                this.$refs.errorAlert.showAlert();
            }
        },
        async onSaveShippingFeeClick() {
            try {
                const requestUrl = `/api/staff/marketplaceOrder/updateShippingFee`;
                const requestData = {
                    orderId: this.data.marketplaceOrder.orderId,
                    shippingFeeAmount: this.data.marketplaceOrder.shippingFeeAmount
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                if (response.success) {
                    this.data.errorAlertMessage = 'Shipping fee updated successfully!';
                    this.$refs.errorAlert.showAlert('success');
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to update shipping fee';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.errorAlertMessage = 'An error occurred while updating the shipping fee';
                this.$refs.errorAlert.showAlert();
            }
        }
    }
});
</script>

<style scoped>
.validation-error {
    color: red;
}
</style>
